import {nextTick} from 'vue';
import {useLogger} from '@/ts/composables/pure/use-logger';

/**
 * Walks up from `start` to find the nearest scrollable ancestor. The walk
 * matches the runtime check we use everywhere else: `overflowY: scroll`, or
 * `overflowY: auto` when the content actually overflows. Exported so callers
 * that need to manipulate scroll position directly (e.g. instant jumps before
 * a smooth scroll) don't have to duplicate the walk.
 */
export function findScrollContainer(start: HTMLElement): HTMLElement | null {
  let el: HTMLElement | null = start;
  while (el) {
    const {overflowY} = window.getComputedStyle(el);
    if (overflowY === 'scroll' || (overflowY === 'auto' && el.scrollHeight > el.clientHeight)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * Returns a function that smoothly scrolls the nearest scrollable ancestor so
 * the resolved target element's top sits near the top of the scroll container,
 * with `reserveSpace` pixels of breathing room above it. The target is resolved
 * inside `nextTick` so callers can request the scroll immediately after
 * mutating reactive state that reveals the element — the resolver runs after
 * Vue has patched the DOM and assigned template refs.
 */
export function useScrollToElement(reserveSpace: number): (resolveTarget: () => HTMLElement | null) => void {
  const logger = useLogger();
  return (resolveTarget: () => HTMLElement | null) => {
    void nextTick(() => {
      const target = resolveTarget();
      if (!target) {
        return;
      }
      const scrollContainer = findScrollContainer(target);
      if (!scrollContainer) {
        return;
      }
      const containerRect = scrollContainer.getBoundingClientRect();
      const targetRect = target.getBoundingClientRect();
      const top = scrollContainer.scrollTop + (targetRect.top - containerRect.top) - reserveSpace;
      logger.debug(`Scrolling to ${top}`)();
      scrollContainer.scrollTo({top, behavior: 'smooth'});
    });
  };
}

/** A rectangle's vertical extent, in viewport coordinates. */
export interface VerticalExtent {
  top: number;
  bottom: number;
}

/** Inputs for {@link revealScrollTop}, all in viewport coordinates bar `scrollTop`. */
export interface RevealGeometry {
  container: VerticalExtent & {scrollTop: number};
  field: VerticalExtent;
  /** Bottom edge of anything pinned over the top of the container — a collapsing banner. */
  overlayTop?: number | null;
  /** Top edge of anything pinned over the bottom of the container — a sticky footer. */
  overlayBottom?: number | null;
  /** Space to keep between the field and an edge of the band. */
  gap: number;
}

/**
 * Where the container has to be scrolled for `field` to sit in the band the reader can see, or
 * null when it already does and nothing should move.
 *
 * The band is the container minus whatever is pinned over it: a footer with a solid background
 * hides content just as surely as the edge of the scrollport does, and the browser's own focus
 * scroll accounts for neither.
 *
 * A field that fits is centred rather than nudged clear by the smallest amount that works: fields
 * grow as they take focus, no single moment offers an exact measurement, and being off-centre by
 * half of that growth is invisible where a minimal nudge measured too early is not. A field taller
 * than the band cannot be centred — that would cut off its label and the top of its message, which
 * is worse than what the browser does unaided — so it is aligned to the top of the band instead.
 */
export function revealScrollTop({container, field, overlayTop, overlayBottom, gap}: RevealGeometry): number | null {
  const visibleTop = Math.max(container.top, overlayTop ?? container.top);
  const visibleBottom = Math.min(container.bottom, overlayBottom ?? container.bottom);
  const band = visibleBottom - visibleTop;
  const fitsInBand = field.bottom - field.top <= band - (2 * gap);

  if (fitsInBand && field.top >= visibleTop + gap && field.bottom <= visibleBottom - gap) {
    return null;
  }

  const target = fitsInBand
    ? container.scrollTop + ((field.top + field.bottom) / 2) - ((visibleTop + visibleBottom) / 2)
    : container.scrollTop + (field.top - visibleTop) - gap;

  // Sub-pixel differences are not worth a scroll, and returning one would have a field that cannot
  // fit re-scrolling on every focus.
  return Math.abs(target - container.scrollTop) < 1 ? null : target;
}
